import { clsx } from 'clsx';
import { format, parseISO } from 'date-fns';
import React from 'react';
import { twMerge } from 'tailwind-merge';
import type { TimeCardList } from '../../../../../js/api';
import { useCurrencyFormatter } from '../../../../../js/app/hooks/use-currency-formatter';
import { StatusPill } from '../../../components/StatusPill';
import { WorkflowScopes } from '../../../constants/workflows';
import { formatDuration } from '../../../utils/date';
import { getFacilityLocationDisplayValue } from '../../../utils/facility';

const DATE_FORMAT = 'LLL d, yyyy';

export const TimecardCard: React.FC<{
  timecard: TimeCardList;
  isSelected?: boolean;
}> = ({ timecard, isSelected = false }) => {
  const dateRange = [
    timecard.start_on ? format(parseISO(timecard.start_on), DATE_FORMAT) : null,
    timecard.end_on ? format(parseISO(timecard.end_on), DATE_FORMAT) : null,
  ]
    .filter(Boolean)
    .join(' - ');
  const { formatCurrencyPostfix } = useCurrencyFormatter({ postfix: '', placeholder: '-' });

  return (
    <div
      className={twMerge(
        clsx(
          'tw:h-full tw:bg-white tw:rounded-lg tw:shadow-sm tw:border tw:border-zinc-200 tw:border-l-stoplight-success tw:border-l-6 tw:hover:shadow-md tw:hover:bg-muted tw:transition-shadow tw:cursor-pointer',
          isSelected && 'tw:!bg-muted tw:!shadow-md'
        )
      )}
    >
      <div className="tw:p-3">
        <div className="tw:flex tw:justify-between tw:items-start tw:mb-3">
          <div>
            <i className="far fa-lg fa-clock tw:text-zinc-700 tw:mr-1" />
            <span className="tw:text-sm tw:text-zinc-700">{timecard.code}</span>
          </div>
          <StatusPill
            scope={WorkflowScopes.TimeCard}
            status={timecard.current_status || 'Unknown'}
          />
        </div>

        <div className="tw:flex tw:flex-col tw:text-sm tw:text-zinc-500 tw:mb-1 tw:gap-y-1">
          <div className="tw:flex tw:items-center tw:gap-1 tw:text-base tw:font-medium tw:text-zinc-950">
            <i className="fas fa-fw fa-hospital tw:text-zinc-950 tw:mr-1" />
            <span>{timecard.facility.name}</span>
          </div>
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="fas fa-fw fa-map-marker-alt tw:text-zinc-500 tw:mr-1" />
            <span>{getFacilityLocationDisplayValue(timecard.facility)}</span>
          </div>
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="far fa-fw fa-calendar tw:mr-1" />
            <span>{dateRange}</span>
          </div>
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="far fa-fw fa-clock tw:mr-1" />
            {timecard.shift_count} {timecard.shift_count === 1 ? 'shift' : 'shifts'}
            {timecard.total_duration && (
              <span className="">&bull; {formatDuration(timecard.total_duration)}</span>
            )}
          </div>
          <div className="tw:flex tw:items-center tw:gap-1">
            <i className="far fa-fw fa-sack-dollar" />
            {timecard.total_amount && Number(timecard.total_amount) > 0 && (
              <span>{formatCurrencyPostfix(timecard.total_amount)}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
